import React, { useEffect, useState } from 'react';
import type { AppointmentEnrollment } from 'models/enrollments/AppointmentEnrollment';

interface Props {
  enrollment: AppointmentEnrollment;
  isTeamMember?: boolean;
  isDisabled?: boolean;
  onUpdateAppointment?: (appointmentTimeUtc: string | null, location: string | null, durationMinutes?: number) => Promise<void>;
  onSuggestTimes?: (times: string[]) => Promise<void>;
  onSubmitAttendance?: (outcome: string, notes?: string) => Promise<void>;
}

type AttendanceOutcome = 'Continue' | 'NoShow' | 'Reschedule' | 'AddAnother';

const OUTCOME_LABELS: Record<AttendanceOutcome, string> = {
  Continue: 'Customer Attended',
  NoShow: 'No Show',
  Reschedule: 'Rescheduled',
  AddAnother: 'Additional Appointment',
};

const OUTCOME_BADGE_COLORS: Record<AttendanceOutcome, string> = {
  Continue: 'bg-green-100 text-green-800',
  NoShow: 'bg-red-100 text-red-800',
  Reschedule: 'bg-amber-100 text-amber-800',
  AddAnother: 'bg-blue-100 text-blue-800',
};

const OUTCOME_BUTTON_COLORS: Record<AttendanceOutcome, string> = {
  Continue: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
  NoShow: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  Reschedule: 'bg-amber-500 hover:bg-amber-600 focus:ring-amber-500',
  AddAnother: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
};

function toLocalDatetimeValue(utcString: string | null): string {
  if (!utcString) return '';
  const d = new Date(utcString);
  if (isNaN(d.getTime())) return '';
  // Format as YYYY-MM-DDTHH:mm in local time
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDisplayDate(utcString: string | null): string {
  if (!utcString) return '';
  const d = new Date(utcString);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    dateStyle: 'full',
    timeStyle: 'short',
  });
}

function parseSuggestedTimes(json: string | null): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const AppointmentEnrollmentView: React.FC<Props> = ({
  enrollment,
  isTeamMember = false,
  isDisabled = false,
  onUpdateAppointment,
  onSuggestTimes,
  onSubmitAttendance,
}) => {
  const [now, setNow] = useState(new Date());

  // Tick `now` every 30s so the view auto-switches from "before" to "attendance"
  // when the appointment time passes while the page is open.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const hasAppointmentTime = !!enrollment.appointmentTimeUtc;
  const appointmentDate = enrollment.appointmentTimeUtc ? new Date(enrollment.appointmentTimeUtc) : null;
  const isBeforeAppointment = !hasAppointmentTime || (appointmentDate != null && appointmentDate > now);
  const isAttendancePhase = hasAppointmentTime && appointmentDate != null && appointmentDate <= now && !enrollment.attendanceOutcome;
  const isComplete = !!enrollment.attendanceOutcome;

  // --- View A: Team — Before Appointment (Update Time/Location) ---
  if (isTeamMember && isBeforeAppointment) {
    return (
      <TeamBeforeAppointmentView
        enrollment={enrollment}
        isDisabled={isDisabled}
        onUpdateAppointment={onUpdateAppointment}
      />
    );
  }

  // --- View C: Team — Attendance Phase ---
  if (isTeamMember && isAttendancePhase) {
    return (
      <TeamAttendanceView
        enrollment={enrollment}
        isDisabled={isDisabled}
        onSubmitAttendance={onSubmitAttendance}
      />
    );
  }

  // --- View D: Read-Only (Attendance Submitted) ---
  if (isComplete) {
    return (
      <ReadOnlyView
        enrollment={enrollment}
        isTeamMember={isTeamMember}
      />
    );
  }

  // --- View B: Customer — Before Appointment (Suggest Times) ---
  return (
    <CustomerBeforeAppointmentView
      enrollment={enrollment}
      isDisabled={isDisabled}
      onSuggestTimes={onSuggestTimes}
    />
  );
};

// ──────────────────────────────────────────────
// View A — Team: Before Appointment
// ──────────────────────────────────────────────

interface TeamBeforeProps {
  enrollment: AppointmentEnrollment;
  isDisabled: boolean;
  onUpdateAppointment?: (appointmentTimeUtc: string | null, location: string | null, durationMinutes?: number) => Promise<void>;
}

const TeamBeforeAppointmentView: React.FC<TeamBeforeProps> = ({
  enrollment,
  isDisabled,
  onUpdateAppointment,
}) => {
  const [dateTime, setDateTime] = useState(
    toLocalDatetimeValue(enrollment.appointmentTimeUtc ?? new Date().toISOString()),
  );
  const [location, setLocation] = useState(enrollment.location ?? '');
  const [duration, setDuration] = useState(enrollment.durationMinutes ?? 60);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasExistingAppointment = !!enrollment.appointmentTimeUtc;

  const handleUpdate = async () => {
    if (!onUpdateAppointment) return;
    setSaving(true);
    setError(null);
    try {
      const utcValue = dateTime ? new Date(dateTime).toISOString() : null;
      await onUpdateAppointment(utcValue, location.trim() || null, duration);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update appointment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const canUpdate = dateTime.trim().length > 0 && !isDisabled && !saving && !!onUpdateAppointment;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
      <h2 className="text-lg font-semibold text-gray-800">Set Appointment</h2>

      {hasExistingAppointment ? (
        <AppointmentSummary enrollment={enrollment} />
      ) : (
        <p className="text-sm text-gray-500 italic">
          Awaiting team to set appointment time
        </p>
      )}

      <div>
        <label htmlFor="appt-datetime" className="block text-sm font-medium text-gray-700 mb-1">
          Appointment Date &amp; Time
        </label>
        <input
          id="appt-datetime"
          type="datetime-local"
          value={dateTime}
          onChange={(e) => setDateTime(e.target.value)}
          disabled={isDisabled || saving}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50"
        />
      </div>

      <div>
        <label htmlFor="appt-location" className="block text-sm font-medium text-gray-700 mb-1">
          Location{' '}
          <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          id="appt-location"
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          disabled={isDisabled || saving}
          placeholder="e.g. Office, Google Meet link, phone number"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50"
        />
      </div>

      <div>
        <label htmlFor="appt-duration" className="block text-sm font-medium text-gray-700 mb-1">
          Duration (minutes)
        </label>
        <input
          id="appt-duration"
          type="number"
          min={15}
          step={15}
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
          disabled={isDisabled || saving}
          className="shadow appearance-none border rounded w-24 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50"
        />
      </div>

      <button
        type="button"
        onClick={handleUpdate}
        disabled={!canUpdate}
        className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? 'Updating...' : 'Update Appointment'}
      </button>

      {error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// View B — Customer: Before Appointment (Suggest Times)
// ──────────────────────────────────────────────

interface CustomerBeforeProps {
  enrollment: AppointmentEnrollment;
  isDisabled: boolean;
  onSuggestTimes?: (times: string[]) => Promise<void>;
}

const CustomerBeforeAppointmentView: React.FC<CustomerBeforeProps> = ({
  enrollment,
  isDisabled,
  onSuggestTimes,
}) => {
  const [timeInputs, setTimeInputs] = useState<string[]>(['']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const existingSuggestions = parseSuggestedTimes(enrollment.suggestedTimesJson);
  const hasExistingAppointment = !!enrollment.appointmentTimeUtc;

  const handleAddTime = () => {
    setTimeInputs((prev) => [...prev, '']);
  };

  const handleRemoveTime = (index: number) => {
    setTimeInputs((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleTimeChange = (index: number, value: string) => {
    setTimeInputs((prev) => prev.map((t, i) => (i === index ? value : t)));
  };

  const handleSubmitSuggestions = async () => {
    if (!onSuggestTimes) return;
    const times = timeInputs
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => new Date(t).toISOString());
    if (times.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSuggestTimes(times);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to submit suggestions. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = timeInputs.some((t) => t.trim().length > 0) && !isDisabled && !submitting && !!onSuggestTimes;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
      <h2 className="text-lg font-semibold text-gray-800">Appointment</h2>

      {hasExistingAppointment ? (
        <AppointmentSummary enrollment={enrollment} />
      ) : (
        <p className="text-sm text-gray-500 italic">Not yet scheduled</p>
      )}

      {/* Existing Suggestions */}
      {existingSuggestions.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Your Suggested Times</h3>
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
            {existingSuggestions.map((t, i) => (
              <li key={i}>{formatDisplayDate(t)}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Suggest Available Times */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Suggest Available Times</h3>
        <div className="space-y-2">
          {timeInputs.map((value, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="datetime-local"
                value={value}
                onChange={(e) => handleTimeChange(index, e.target.value)}
                disabled={isDisabled || submitting}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50"
              />
              {timeInputs.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveTime(index)}
                  disabled={isDisabled || submitting}
                  className="shrink-0 inline-flex items-center justify-center rounded p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Remove time slot"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={handleAddTime}
          disabled={isDisabled || submitting}
          className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Another Time
        </button>
      </div>

      <button
        type="button"
        onClick={handleSubmitSuggestions}
        disabled={!canSubmit}
        className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Submitting...' : 'Submit Suggestions'}
      </button>

      {error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// View C — Team: Attendance Phase
// ──────────────────────────────────────────────

interface TeamAttendanceProps {
  enrollment: AppointmentEnrollment;
  isDisabled: boolean;
  onSubmitAttendance?: (outcome: string, notes?: string) => Promise<void>;
}

const attendanceOutcomes: AttendanceOutcome[] = ['Continue', 'NoShow', 'Reschedule', 'AddAnother'];

const TeamAttendanceView: React.FC<TeamAttendanceProps> = ({
  enrollment,
  isDisabled,
  onSubmitAttendance,
}) => {
  const [selectedOutcome, setSelectedOutcome] = useState<AttendanceOutcome | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!selectedOutcome || !onSubmitAttendance) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmitAttendance(selectedOutcome, notes.trim() || undefined);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to submit attendance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !!selectedOutcome && !isDisabled && !submitting && !!onSubmitAttendance;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
      <h2 className="text-lg font-semibold text-gray-800">Record Attendance</h2>

      <AppointmentSummary enrollment={enrollment} />

      {/* Segmented Outcome Buttons */}
      <div>
        <p className="block text-sm font-medium text-gray-700 mb-2">Outcome</p>
        <div className="flex flex-wrap gap-2">
          {attendanceOutcomes.map((outcome) => {
            const isSelected = selectedOutcome === outcome;
            return (
              <button
                key={outcome}
                type="button"
                onClick={() => setSelectedOutcome(outcome)}
                disabled={isDisabled || submitting}
                className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-white transition-all
                  ${OUTCOME_BUTTON_COLORS[outcome]}
                  ${isSelected ? 'ring-2 ring-offset-2 ring-gray-800' : ''}
                  disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {outcome === 'Continue' && 'Continue'}
                {outcome === 'NoShow' && 'No Show'}
                {outcome === 'Reschedule' && 'Reschedule'}
                {outcome === 'AddAnother' && 'Add Another'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="attendance-notes" className="block text-sm font-medium text-gray-700 mb-1">
          Notes{' '}
          <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          id="attendance-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isDisabled || submitting}
          rows={3}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50"
        />
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Submitting...' : 'Submit Attendance'}
      </button>

      {error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// View D — Read-Only (Attendance Submitted)
// ──────────────────────────────────────────────

interface ReadOnlyProps {
  enrollment: AppointmentEnrollment;
  isTeamMember: boolean;
}

const ReadOnlyView: React.FC<ReadOnlyProps> = ({ enrollment }) => {
  const outcome = enrollment.attendanceOutcome as AttendanceOutcome | null;
  const badgeColor = outcome ? OUTCOME_BADGE_COLORS[outcome] : 'bg-gray-100 text-gray-800';
  const label = outcome ? OUTCOME_LABELS[outcome] : enrollment.attendanceOutcome ?? 'Unknown';

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
      <h2 className="text-lg font-semibold text-gray-800">Appointment</h2>

      <AppointmentSummary enrollment={enrollment} />

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700">Outcome:</span>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>
          {label}
        </span>
      </div>

      {enrollment.attendanceOutcome && (
        <div className="text-sm text-gray-600 bg-gray-50 rounded p-3">
          <p>
            <span className="font-medium">Recorded:</span> {label}
            {enrollment.appointmentTimeUtc && (
              <span className="ml-1 text-gray-400">
                on {formatDisplayDate(enrollment.appointmentTimeUtc)}
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// Shared — Appointment Summary Card
// ──────────────────────────────────────────────

interface SummaryProps {
  enrollment: AppointmentEnrollment;
}

const AppointmentSummary: React.FC<SummaryProps> = ({ enrollment }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1 text-sm">
    {enrollment.appointmentTimeUtc && (
      <div className="flex justify-between">
        <span className="text-gray-500">Date &amp; Time</span>
        <span className="text-gray-800 font-medium">
          {formatDisplayDate(enrollment.appointmentTimeUtc)}
        </span>
      </div>
    )}
    {enrollment.location && (
      <div className="flex justify-between">
        <span className="text-gray-500">Location</span>
        <span className="text-gray-800 font-medium">{enrollment.location}</span>
      </div>
    )}
    <div className="flex justify-between">
      <span className="text-gray-500">Duration</span>
      <span className="text-gray-800 font-medium">{enrollment.durationMinutes} min</span>
    </div>
  </div>
);

export default AppointmentEnrollmentView;
